import { Button, ButtonTheme } from 'components/Button';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useSelector } from 'hooks/useSelector';
import { downscaleProportionally } from 'lib/scaleWidthHeight';
import { theme } from 'lib/theme';
import React from 'react';
import { LayoutChangeEvent, Pressable, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  doClosePlans,
  doPlanUnsetPin,
  doPlansLoadPlan,
  doPlansSetSelectedPinId,
  doPlansUpdatePin,
  doPlansUsePin,
} from 'store/plan/actions';
import {
  selectAfterActionFind,
  selectPlansFindNoun,
  selectPlansPlan,
  selectPlansPinIsDirty,
  selectPlansSelectedPinId,
  selectSelectionKey,
  selectPlansPinPlanId,
} from 'store/plan/selectors';

import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { ViewZoom, ViewZoomRef } from 'components/ViewZoom';
import { PIN_DIM } from './Pin';
import { PinsMain, PinsRef } from './PinsMain';
import { PinsOther } from './PinsOther';
import { CanvasImage, CanvasImageRef } from './CanvasImage';
import { PageHeader } from 'components/PageHeader';
import { IconButton } from 'components/IconButton';
import { ButtonGroup } from 'components/ButtonGroup';

export const Canvas: React.FC<ModalScreenProps<ModalScreens.PLANS>> = () => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const isDirtyRef = React.useRef(false);
  const [canvasDims, setCanvasDims] = React.useState({
    originalHeight: 0,
    width: 0,
  });
  const pinsRef = React.useRef<PinsRef>(null);
  const pinsOtherRef = React.useRef<PinsRef>(null);
  const zoomableViewRef = React.useRef<ViewZoomRef>(null);
  const canvasImageRef = React.useRef<CanvasImageRef>(null);
  const canvasResetFrameRef = React.useRef<number | undefined>(undefined);

  const zoomScale = React.useRef(1);

  const selectedIndex = useSelector(selectPlansSelectedPinId);
  const pinDirty = useSelector(selectPlansPinIsDirty);

  const plan = useSelector(selectPlansPlan);
  const pinPlanId = useSelector(selectPlansPinPlanId);
  const pinInPlan = pinPlanId && pinPlanId === plan?.id;

  const selectionKey = useSelector(selectSelectionKey);
  const afterActionFind = useSelector(selectAfterActionFind);
  const findNoun = useSelector(selectPlansFindNoun);

  const isCreate = !!selectionKey;
  const isCreateMode = isCreate && !pinInPlan;
  const showPinAddedText = isCreate && !!pinInPlan;
  const showGoToPlanId = isCreate && !pinInPlan ? pinPlanId : undefined;
  const showRemovePinBtn = isCreate && !!pinPlanId;

  const isFind = !!afterActionFind;
  const isFoundMode = isFind && selectedIndex !== undefined;

  const image = plan?.image || {
    base64: '',
    url: '',
    height: 0,
    width: 0,
    type: 'image',
    page: 0,
    isUsingThumbnail: false,
    isFileMissing: true,
  };

  const [headerHeight, setHeaderHeight] = React.useState(0);

  const handleHeaderChangeHeight = React.useCallback((height: number) => {
    setHeaderHeight(height);
  }, []);

  const canvasPadding = PIN_DIM.height + theme.padding;
  const canvasHeight = Math.max(0, canvasDims.originalHeight - headerHeight);

  const downscaledImageSize = downscaleProportionally(
    image.width,
    image.height,
    Math.max(0, canvasDims.width - canvasPadding * 2),
    Math.max(0, canvasHeight - canvasPadding * 2),
  );

  const calculatedSize = {
    // canvasHeight: canvasDims.height,
    // canvasWidth: canvasDims.width,
    imageHeight: downscaledImageSize.height,
    imageWidth: downscaledImageSize.width,
  };

  const handleOnLayout = React.useCallback(
    ({
      nativeEvent: {
        layout: { width, height },
      },
    }: LayoutChangeEvent) =>
      setCanvasDims({
        originalHeight: height,
        width,
      }),
    [],
  );

  const handlePinMove = React.useCallback(
    ({ x, y }: { x: number; y: number }) => {
      isDirtyRef.current = true;
      dispatch(
        doPlansUpdatePin({
          name: '',
          planId: plan?.id || '',
          x: +x.toFixed(12),
          y: +y.toFixed(12),
        }),
      );
    },
    [dispatch, plan?.id],
  );

  const handlePinPress = React.useCallback(
    (id: string) => {
      dispatch(doPlansSetSelectedPinId(id));
    },
    [dispatch],
  );

  const handleMainPinPress = React.useCallback(() => {
    zoomableViewRef.current?.setEnabled(false);
  }, [zoomableViewRef]);

  const handleMainPinPressUp = React.useCallback(() => {
    zoomableViewRef.current?.setEnabled(true);
  }, [zoomableViewRef]);

  const handleAddPin = ({ x, y }: { x: number; y: number }) => {
    if (isCreateMode) {
      dispatch(
        doPlansUpdatePin({
          name: '',
          planId: plan?.id || '',
          x: +((x / calculatedSize.imageWidth) * 100).toFixed(12),
          y: +((y / calculatedSize.imageHeight) * 100).toFixed(12),
        }),
      );
    }
  };

  const handleCanvasPan = React.useCallback(
    ({ x, y }: { x: number; y: number }) => {
      pinsRef.current?.panChange({ x, y });
      pinsOtherRef.current?.panChange({ x, y });
    },
    [pinsRef, pinsOtherRef],
  );

  const handleCanvasZoomChange = React.useCallback(
    ({ scale }: { scale: number }) => {
      zoomScale.current = scale;
      if (image.type === 'pdf' || image.type === 'pdfpage') {
        canvasImageRef.current?.setPdfScale(scale);
      }
      pinsRef.current?.scaleChange(scale);
      pinsOtherRef.current?.scaleChange(scale);
    },
    [canvasImageRef, image.type, pinsOtherRef, pinsRef],
  );

  const handleZoomIn = () => {
    zoomableViewRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    zoomableViewRef.current?.zoomOut();
  };

  const handleZoomReset = () => {
    zoomableViewRef.current?.zoomReset();
  };

  const runAfterCanvasReset = React.useCallback((callback: () => void) => {
    canvasImageRef.current?.prepareForClose();
    zoomableViewRef.current?.zoomReset();

    if (canvasResetFrameRef.current !== undefined) {
      cancelAnimationFrame(canvasResetFrameRef.current);
    }

    canvasResetFrameRef.current = requestAnimationFrame(() => {
      // Wait until the identity transform has been painted before allowing
      // Fabric/native navigation to recycle the transformed PDF view.
      canvasResetFrameRef.current = requestAnimationFrame(() => {
        canvasResetFrameRef.current = undefined;
        callback();
      });
    });
  }, []);

  const handleUseSelectedPinPress = React.useCallback(() => {
    runAfterCanvasReset(() => dispatch(doPlansUsePin()));
  }, [dispatch, runAfterCanvasReset]);

  const handleClosePlans = React.useCallback(() => {
    runAfterCanvasReset(() => dispatch(doClosePlans()));
  }, [dispatch, runAfterCanvasReset]);

  React.useEffect(
    () => () => {
      if (canvasResetFrameRef.current !== undefined) {
        cancelAnimationFrame(canvasResetFrameRef.current);
      }
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    if (pinDirty) {
      confirmUnsaved({
        onSave: handleUseSelectedPinPress,
        onDiscard: handleClosePlans,
      });
      return;
    }
    handleClosePlans();
  }, [confirmUnsaved, handleClosePlans, handleUseSelectedPinPress, pinDirty]);

  useEsc(handleClose);

  if (!plan) {
    return null;
  }

  return (
    <Page
      paddingBottom={0}
      paddingTop={0}
      onHeaderChangeHeight={handleHeaderChangeHeight}
      header={
        isCreate ? (
          <PageHeader
            title={plan.name}
            subtitle={showPinAddedText ? 'Pin Added' : 'Tap to Add Pin'}
            onPressCancelButton={handleClose}
            onPressSaveButton={handleUseSelectedPinPress}
          />
        ) : (
          <PageHeader
            title={plan.name}
            subtitle={findNoun ? `Find ${findNoun}` : undefined}
            onPressCancelButton={handleClose}
            onPressSaveButton={
              isFind
                ? isFoundMode
                  ? handleUseSelectedPinPress
                  : null
                : undefined
            }
          />
        )
      }
    >
      <View
        onLayout={handleOnLayout}
        style={{
          flex: 1,
          position: 'relative',
        }}
      >
        {!!canvasHeight && (
          <ViewZoom
            ref={zoomableViewRef}
            onScaleChange={handleCanvasZoomChange}
            onPan={handleCanvasPan}
            onSingleTap={handleAddPin}
            animate={false}
            contentsSize={{
              width: calculatedSize.imageWidth,
              height: calculatedSize.imageHeight,
            }}
          >
            <View
              style={{
                backgroundColor: 'white',
                shadowColor: 'black',
                shadowOffset: { height: 0, width: 0 },
                shadowOpacity: 0.2,
                shadowRadius: 50,
                pointerEvents: 'none',
              }}
            >
              <CanvasImage
                ref={canvasImageRef}
                base64={image.base64}
                url={image.url}
                width={calculatedSize.imageWidth}
                height={calculatedSize.imageHeight}
                page={image.page}
                type={image.type}
              />
            </View>
          </ViewZoom>
        )}
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          pointerEvents="box-none"
        >
          <PinsOther
            ref={pinsOtherRef}
            width={calculatedSize.imageWidth}
            height={calculatedSize.imageHeight}
            scale={zoomScale.current}
            onPinPress={isFind ? handlePinPress : undefined}
          />
          <PinsMain
            ref={pinsRef}
            width={calculatedSize.imageWidth}
            height={calculatedSize.imageHeight}
            scale={zoomScale.current}
            onPinMove={isCreate ? handlePinMove : undefined}
            onPinPress={handleMainPinPress}
            onPinPressUp={handleMainPinPressUp}
            image={image}
          />
        </View>
        <Pressable
          style={{
            position: 'absolute',
            right: 16 - 7,
            top: headerHeight + (16 - 7),
            padding: 7,
            zIndex: 999,
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.padding,
          }}
        >
          <ButtonGroup style={{ flexDirection: 'column' }}>
            {(isCreate || isFind) && (
              <IconButton
                icon={ICONS.ListLight}
                onPress={() =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.PLANLIST,
                      params: {},
                    }),
                  )
                }
              />
            )}
            <IconButton
              icon={ICONS.CircleInfoLight}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.PLANINFO,
                    params: {},
                  }),
                )
              }
            />
          </ButtonGroup>

          <ButtonGroup style={{ flexDirection: 'column' }}>
            <IconButton icon={ICONS.PlusLight} onPress={handleZoomIn} />
            <IconButton
              icon={ICONS.CircleDotSolid}
              onPress={handleZoomReset}
              iconSize={12}
            />
            <IconButton icon={ICONS.MinusLight} onPress={handleZoomOut} />
          </ButtonGroup>
        </Pressable>
        {showRemovePinBtn && (
          <ButtonGroup
            style={{
              position: 'absolute',
              zIndex: 1,
              bottom: theme.paddingVertical,
              alignSelf: 'center',
              flexDirection: 'row',
              gap: theme.padding,
            }}
          >
            <Button
              text="Remove Pin"
              onPress={() => dispatch(doPlanUnsetPin())}
              buttonTheme={ButtonTheme.secondary}
              destructive
              style={{ opacity: 0.9 }}
            />
            {!!showGoToPlanId && (
              <Button
                text={`Go To Pin`}
                onPress={() =>
                  dispatch(doPlansLoadPlan({ id: showGoToPlanId }))
                }
                buttonTheme={ButtonTheme.secondary}
                style={{ opacity: 0.9 }}
              />
            )}
          </ButtonGroup>
        )}
      </View>
    </Page>
  );
};
